import { AutocompleteProvider } from "../../types/enums";

// Google answers a key that does not exist with a 400, not a 403, and names it only in the body.
const INVALID_KEY_REASON = "API_KEY_INVALID";

// A stalled request never settles on its own, so without this the failover would never run.
const REQUEST_TIMEOUT_MS = 8_000;

/**
 * Separates "this provider is broken" from "this provider found nothing" so the failover chain
 * only moves on for real faults. A no-result response is not an error.
 */
export class GeoSearchError extends Error {
    public readonly providerId: AutocompleteProvider;
    public readonly status: number | null;
    public readonly reason: string | null;

    constructor(
        providerId: AutocompleteProvider,
        message: string,
        status: number | null = null,
        reason: string | null = null
    ) {
        super(message);
        this.name = "GeoSearchError";
        this.providerId = providerId;
        this.status = status;
        this.reason = reason;
    }

    /**
     * A key, quota or outage problem — the provider stays broken for the next call too.
     * A 404 or a malformed query is not, so it must not park a healthy provider.
     */
    public get isProviderUnhealthy(): boolean {
        if (this.status === null || this.reason === INVALID_KEY_REASON)
            return true;

        return this.status === 401
            || this.status === 403
            || this.status === 429
            || this.status >= 500;
    }
}

export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === "AbortError";
}

export async function fetchJsonAsync<T>(
    providerId: AutocompleteProvider,
    url: string,
    init?: RequestInit
): Promise<T> {
    const deadline = new RequestDeadline(init?.signal ?? null);

    try {
        return await fetchJsonWithoutTimeoutAsync<T>(providerId, url, { ...init, signal: deadline.signal });
    }
    catch (error) {
        // The timeout aborts the request too, but unlike a newer keystroke it is the provider's fault.
        if (deadline.hasExpired) {
            const message = `No answer from ${describeEndpoint(url)} within ${REQUEST_TIMEOUT_MS} ms`;
            throw new GeoSearchError(providerId, message);
        }

        throw error;
    }
    finally {
        deadline.clear();
    }
}

/** One signal that aborts when either the caller cancels or the provider takes too long. */
class RequestDeadline {
    private readonly _controller = new AbortController();
    private readonly _callerSignal: AbortSignal | null;
    private readonly _timeoutId: number;
    private _hasExpired = false;

    constructor(callerSignal: AbortSignal | null) {
        this._callerSignal = callerSignal;

        if (callerSignal?.aborted)
            this._controller.abort();

        callerSignal?.addEventListener("abort", this._abortWithCaller);

        this._timeoutId = window.setTimeout(() => {
            this._hasExpired = true;
            this._controller.abort();
        }, REQUEST_TIMEOUT_MS);
    }

    public get signal(): AbortSignal {
        return this._controller.signal;
    }

    public get hasExpired(): boolean {
        return this._hasExpired;
    }

    public clear(): void {
        window.clearTimeout(this._timeoutId);
        this._callerSignal?.removeEventListener("abort", this._abortWithCaller);
    }

    private readonly _abortWithCaller = (): void => {
        this._controller.abort();
    };
}

async function fetchJsonWithoutTimeoutAsync<T>(
    providerId: AutocompleteProvider,
    url: string,
    init: RequestInit
): Promise<T> {
    let response: Response;

    try {
        response = await fetch(url, init);
    }
    catch (error) {
        if (isAbortError(error))
            throw error;

        throw new GeoSearchError(providerId, `Request to ${describeEndpoint(url)} failed`, null);
    }

    if (!response.ok) {
        const reason = await readErrorReasonAsync(response);
        const message = `HTTP ${response.status} from ${describeEndpoint(url)}`;
        throw new GeoSearchError(providerId, message, response.status, reason);
    }

    return await response.json() as T;
}

// The query string carries the provider's API key, which must not reach the console or telemetry.
function describeEndpoint(url: string): string {
    const { origin, pathname } = new URL(url, window.location.href);
    return origin + pathname;
}

type GoogleErrorBody = {
    error?: { details?: Array<{ reason?: string }> }
};

/** Google puts the reason in the error body; any other body, or none, has no reason. */
async function readErrorReasonAsync(response: Response): Promise<string | null> {
    try {
        const body = await response.json() as GoogleErrorBody | null;
        return body?.error?.details?.find(detail => detail.reason)?.reason ?? null;
    }
    catch (error) {
        if (isAbortError(error))
            throw error;

        return null;
    }
}
